<template>
  <transition name="help-center-fade">
    <div
      v-if="visible"
      class="help-center"
      role="presentation"
      @mousedown.self="close"
    >
      <section
        ref="dialog"
        class="help-center__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-center-title"
        tabindex="-1"
        @mousedown.stop
      >
        <header class="help-center__header">
          <div>
            <h2 id="help-center-title">帮助中心</h2>
            <p>{{ totalCount }} 篇常见问题 · 覆盖开户、API、费用、风控全流程</p>
          </div>

          <div class="help-center__header-actions">
            <label class="help-center__search">
              <i class="el-icon-search" aria-hidden="true" />
              <input
                ref="search"
                v-model.trim="searchKeyword"
                type="search"
                maxlength="40"
                placeholder="搜索问题关键词..."
                aria-label="搜索帮助中心问题"
              />
              <button
                v-if="searchKeyword"
                type="button"
                aria-label="清空搜索"
                @click="clearSearch"
              >
                <i class="el-icon-circle-close" aria-hidden="true" />
              </button>
            </label>
            <button class="help-center__close" type="button" aria-label="关闭帮助中心" @click="close">
              <i class="el-icon-close" aria-hidden="true" />
            </button>
          </div>
        </header>

        <div class="help-center__body">
          <aside class="help-center__sidebar" aria-label="帮助分类">
            <nav>
              <button
                v-for="category in categories"
                :key="category.key"
                type="button"
                :class="{ active: !searchKeyword && activeCategory === category.key }"
                @click="selectCategory(category.key)"
              >
                <span>{{ category.name }}</span>
                <b>{{ category.items.length }}</b>
              </button>
            </nav>

            <div class="help-center__contact">
              <p>没有找到答案？</p>
              <button type="button" @click="openCustomerService">联系客服</button>
            </div>
          </aside>

          <main class="help-center__content">
            <p class="help-center__category-title">{{ contentTitle }}</p>

            <div v-if="visibleItems.length" class="help-center__questions">
              <article
                v-for="item in visibleItems"
                :key="item.id"
                class="help-center__question"
                :class="{ open: openedQuestion === item.id }"
              >
                <button
                  type="button"
                  :aria-expanded="openedQuestion === item.id ? 'true' : 'false'"
                  :aria-controls="`help-answer-${item.id}`"
                  @click="toggleQuestion(item.id)"
                >
                  <span v-html="highlightText(item.question)" />
                  <i class="el-icon-arrow-down" aria-hidden="true" />
                </button>
                <transition name="help-answer">
                  <div
                    v-if="openedQuestion === item.id"
                    :id="`help-answer-${item.id}`"
                    class="help-center__answer"
                  >
                    <p
                      v-for="(paragraph, index) in item.answer"
                      :key="index"
                      v-html="highlightText(paragraph)"
                    />
                    <a
                      v-if="item.link"
                      class="help-center__answer-link"
                      :href="item.link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ item.linkText || '查看详情' }}<i class="el-icon-arrow-right" aria-hidden="true" /></a>
                  </div>
                </transition>
              </article>
            </div>

            <div v-else class="help-center__empty">
              <i class="el-icon-search" aria-hidden="true" />
              <strong>没有找到相关问题</strong>
              <p>换一个关键词试试，或联系在线客服。</p>
              <button type="button" @click="clearSearch">清空搜索</button>
            </div>
          </main>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import createHelpCenterCategories from '@/data/helpCenterData'

export default {
  name: 'HelpCenterModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialCategory: {
      type: String,
      default: ''
    }
  },
  data() {
    const categories = createHelpCenterCategories()
    return {
      categories,
      activeCategory: categories[0].key,
      openedQuestion: '',
      searchKeyword: '',
      previousBodyOverflow: ''
    }
  },
  computed: {
    totalCount() {
      return this.categories.reduce((total, category) => total + category.items.length, 0)
    },
    activeCategoryData() {
      return this.categories.find(category => category.key === this.activeCategory) || this.categories[0]
    },
    visibleItems() {
      const keyword = this.searchKeyword.toLowerCase()
      if (!keyword) return this.activeCategoryData.items

      return this.categories.reduce((items, category) => {
        category.items.forEach(item => {
          const question = this.$t(item.question)
          const answerText = item.answer.map(paragraph => this.$t(paragraph)).join(' ')
          if (`${question} ${answerText}`.toLowerCase().includes(keyword)) {
            items.push(item)
          }
        })
        return items
      }, [])
    },
    contentTitle() {
      if (this.searchKeyword) return `搜索结果 · ${this.visibleItems.length}`
      return this.activeCategoryData.name
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.applyInitialCategory()
        if (!this.isMobileViewport()) {
          this.previousBodyOverflow = document.body.style.overflow
          document.body.style.overflow = 'hidden'
        }
        this.$nextTick(() => {
          if (this.$refs.dialog && !this.isMobileViewport()) {
            this.$refs.dialog.focus({ preventScroll: true })
          }
        })
      } else {
        this.restorePageScroll()
      }
    },
    initialCategory() {
      if (this.visible) this.applyInitialCategory()
    },
    searchKeyword() {
      this.openedQuestion = ''
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
    this.restorePageScroll()
  },
  methods: {
    isMobileViewport() {
      return typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
    },
    restorePageScroll() {
      if (!this.isMobileViewport()) document.body.style.overflow = this.previousBodyOverflow
    },
    applyInitialCategory() {
      const requestedCategory = this.categories.find(category => category.key === this.initialCategory)
      this.activeCategory = requestedCategory ? requestedCategory.key : this.categories[0].key
      this.searchKeyword = ''
      this.openedQuestion = ''
    },
    close() {
      this.$emit('close')
    },
    handleKeydown(event) {
      if (this.visible && event.key === 'Escape') this.close()
    },
    selectCategory(key) {
      this.searchKeyword = ''
      this.activeCategory = key
      this.openedQuestion = ''
    },
    toggleQuestion(id) {
      this.openedQuestion = this.openedQuestion === id ? '' : id
    },
    clearSearch() {
      this.searchKeyword = ''
      this.openedQuestion = ''
      this.$nextTick(() => {
        if (this.$refs.search) this.$refs.search.focus()
      })
    },
    escapeHtml(value) {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }
      return String(value == null ? '' : value).replace(/[&<>"']/g, character => entities[character])
    },
    escapeRegExp(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    highlightText(value) {
      const text = String(this.$t(value == null ? '' : value))
      const keyword = this.searchKeyword.trim()
      if (!keyword) return this.escapeHtml(text)

      const pattern = new RegExp(this.escapeRegExp(keyword), 'gi')
      let result = ''
      let lastIndex = 0
      let match = pattern.exec(text)

      while (match) {
        result += this.escapeHtml(text.slice(lastIndex, match.index))
        result += `<mark class="help-center__highlight">${this.escapeHtml(match[0])}</mark>`
        lastIndex = match.index + match[0].length
        match = pattern.exec(text)
      }

      return result + this.escapeHtml(text.slice(lastIndex))
    },
    openCustomerService() {
      this.close()
      this.$nextTick(() => this.$root.$emit('open-customer-service'))
    }
  }
}
</script>

<style scoped>
.help-center {
  position: fixed;
  z-index: 13200;
  inset: 0;
  display: flex;
  padding: 18px;
  align-items: center;
  justify-content: center;
  background: rgba(20, 17, 38, 0.48);
}

.help-center__panel {
  display: flex;
  width: 860px;
  max-width: 96vw;
  height: min(1020px, calc(100vh - 36px));
  max-height: 96vh;
  flex-direction: column;
  overflow: hidden;
  color: #1b1f2a;
  border: 1px solid #e8ebf2;
  border-radius: 16px;
  outline: none;
  background: #fff;
  box-shadow: 0 24px 64px rgba(20, 16, 45, 0.25);
  font-family: Manrope, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  -webkit-font-smoothing: antialiased;
}

.help-center__header {
  display: flex;
  min-height: 94px;
  padding: 24px 28px 20px;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid #e9edf4;
}

.help-center__header h2 {
  margin: 0;
  color: #1b1f2a;
  font-size: 20px;
  font-weight: 800;
  line-height: 28px;
}

.help-center__header p {
  margin: 2px 0 0;
  color: #98a0af;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 19px;
}

.help-center__header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.help-center__search {
  position: relative;
  display: flex;
  width: 220px;
  height: 38px;
  align-items: center;
  color: #98a0af;
}

.help-center__search > i {
  position: absolute;
  left: 12px;
  z-index: 1;
  font-size: 14px;
}

.help-center__search input {
  width: 100%;
  height: 100%;
  padding: 0 36px 0 34px;
  color: #3a3f4e;
  border: 1px solid #dfe4ed;
  border-radius: 8px;
  outline: none;
  background: #fafbfc;
  font-size: 12.5px;
  font-weight: 400;
  transition: border-color .16s ease, box-shadow .16s ease, background-color .16s ease;
}

.help-center__search input::placeholder {
  color: #a5acba;
  font-weight: 400;
}

.help-center__search input:focus {
  border-color: #8ab2ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(31, 107, 255, 0.1);
}

.help-center__search button {
  position: absolute;
  right: 9px;
  display: inline-flex;
  width: 22px;
  height: 22px;
  padding: 0;
  align-items: center;
  justify-content: center;
  color: #98a0af;
  border: 0;
  border-radius: 50%;
  background: transparent;
}

.help-center__search button:hover {
  color: #667085;
  background: #eef1f6;
}

.help-center__close {
  display: inline-flex;
  width: 28px;
  height: 38px;
  padding: 0;
  align-items: center;
  justify-content: center;
  color: #98a0af;
  border: 0;
  border-radius: 8px;
  outline: none;
  background: transparent;
  font-size: 16px;
  transition: color .16s ease, background-color .16s ease;
}

.help-center__close:hover,
.help-center__close:focus-visible {
  color: #5a6072;
  background: #f2f5fa;
}

.help-center__body {
  display: grid;
  min-height: 0;
  flex: 1 1 auto;
  grid-template-columns: 190px minmax(0, 1fr);
}

.help-center__sidebar {
  display: flex;
  min-height: 0;
  padding: 16px 12px 14px;
  flex-direction: column;
  justify-content: space-between;
  border-right: 1px solid #e9edf4;
  background: #fbfcfe;
}

.help-center__sidebar nav {
  display: grid;
  gap: 2px;
}

.help-center__sidebar nav button {
  display: flex;
  width: 100%;
  height: 42px;
  padding: 0 12px;
  align-items: center;
  justify-content: space-between;
  color: #667085;
  border: 1px solid transparent;
  border-radius: 9px;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  transition: color .16s ease, border-color .16s ease, background-color .16s ease;
}

.help-center__sidebar nav button b {
  color: #98a0af;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.help-center__sidebar nav button:hover {
  color: #1f6bff;
  background: #f2f6ff;
}

.help-center__sidebar nav button.active {
  color: #1f6bff;
  border-color: #b9d1ff;
  background: #eaf2ff;
}

.help-center__sidebar nav button:focus-visible {
  box-shadow: 0 0 0 2px rgba(31, 107, 255, 0.14);
}

.help-center__contact {
  padding: 17px 12px 0;
  border-top: 1px solid #e9edf4;
}

.help-center__contact p {
  margin: 0 0 12px;
  color: #98a0af;
  font-size: 11.5px;
  font-weight: 400;
  line-height: 18px;
}

.help-center__contact button {
  width: 100%;
  height: 37px;
  color: #fff;
  border: 0;
  border-radius: 8px;
  background: #1f6bff;
  font-size: 13px;
  font-weight: 700;
  transition: background-color .16s ease, transform .16s ease;
}

.help-center__contact button:hover {
  background: #1558db;
}

.help-center__contact button:active {
  transform: translateY(1px);
}

.help-center__content {
  min-width: 0;
  min-height: 0;
  padding: 20px 24px 28px 26px;
  overflow-y: auto;
  scrollbar-color: #c7cfdd transparent;
  scrollbar-width: thin;
}

.help-center__content::-webkit-scrollbar {
  width: 6px;
}

.help-center__content::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: #c7cfdd;
}

.help-center__category-title {
  margin: 0 0 11px;
  color: #98a0af;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}

.help-center__questions {
  display: grid;
  gap: 9px;
}

.help-center__question {
  overflow: hidden;
  border: 1px solid #e5e9f0;
  border-radius: 11px;
  background: #fff;
  transition: border-color .16s ease, box-shadow .16s ease;
}

.help-center__question:hover {
  border-color: #cbd8ee;
}

.help-center__question.open {
  border-color: #b9d1ff;
  box-shadow: 0 4px 12px rgba(31, 107, 255, 0.06);
}

.help-center__question > button {
  display: flex;
  width: 100%;
  min-height: 48px;
  padding: 0 15px;
  align-items: center;
  justify-content: space-between;
  color: #1b1f2a;
  border: 0;
  outline: none;
  background: transparent;
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
}

.help-center__question > button:focus-visible {
  box-shadow: inset 0 0 0 2px rgba(31, 107, 255, 0.18);
}

.help-center__question > button i {
  margin-left: 18px;
  color: #98a0af;
  font-size: 12px;
  transition: transform .18s ease, color .18s ease;
}

.help-center__question.open > button i {
  color: #1f6bff;
  transform: rotate(180deg);
}

.help-center__answer {
  padding: 0 15px 14px;
  border-top: 1px solid #f0f2f6;
  background: #fbfcff;
}

.help-center__answer p {
  margin: 11px 0 0;
  color: #5f6678;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.85;
}

.help-center__question /deep/ .help-center__highlight {
  padding: 0 2px;
  color: #1558db;
  border-radius: 3px;
  background: #eaf2ff;
  font: inherit;
}

.help-center__answer-link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 11px;
  color: #1f6bff;
  font-size: 12.5px;
  font-weight: 600;
  text-decoration: none;
}

.help-center__answer-link:hover {
  color: #1558db;
}

.help-center__empty {
  display: flex;
  min-height: 280px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #98a0af;
  text-align: center;
}

.help-center__empty > i {
  margin-bottom: 12px;
  color: #b6bdc9;
  font-size: 28px;
}

.help-center__empty strong {
  color: #5a6072;
  font-size: 14px;
}

.help-center__empty p {
  margin: 6px 0 14px;
  font-size: 12px;
}

.help-center__empty button {
  padding: 7px 15px;
  color: #1f6bff;
  border: 1px solid #bed3ff;
  border-radius: 7px;
  background: #f5f8ff;
  font-size: 12px;
  font-weight: 700;
}

.help-center-fade-enter-active,
.help-center-fade-leave-active {
  transition: opacity .18s ease;
}

.help-center-fade-enter-active .help-center__panel,
.help-center-fade-leave-active .help-center__panel {
  transition: transform .18s ease, opacity .18s ease;
}

.help-center-fade-enter,
.help-center-fade-leave-to {
  opacity: 0;
}

.help-center-fade-enter .help-center__panel,
.help-center-fade-leave-to .help-center__panel {
  opacity: 0;
  transform: translateY(8px) scale(.99);
}

.help-answer-enter-active,
.help-answer-leave-active {
  transition: opacity .15s ease, transform .15s ease;
}

.help-answer-enter,
.help-answer-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 720px) {
  .help-center {
    padding: 10px;
  }

  .help-center__panel {
    width: 100%;
    max-width: none;
    height: calc(100vh - 20px);
    max-height: none;
  }

  .help-center__header {
    gap: 14px;
    padding: 18px;
    flex-direction: column;
  }

  .help-center__header-actions,
  .help-center__search {
    width: 100%;
  }

  .help-center__close {
    position: absolute;
    top: 14px;
    right: 14px;
  }

  .help-center__body {
    display: flex;
    flex-direction: column;
  }

  .help-center__sidebar {
    display: block;
    padding: 10px 12px;
    border-right: 0;
    border-bottom: 1px solid #e9edf4;
  }

  .help-center__sidebar nav {
    display: flex;
    overflow-x: auto;
  }

  .help-center__sidebar nav button {
    width: auto;
    min-width: max-content;
    gap: 12px;
  }

  .help-center__contact {
    display: none;
  }

  .help-center__content {
    padding: 18px 16px 24px;
  }
}
</style>
